import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, CreditCard, Search, ChevronLeft, ChevronRight, ArrowLeft, Shield,
  DollarSign, MessageSquare, Brain, Loader2, CheckCircle, XCircle, RefreshCw, Mail, KeyRound,
  Tag, Plus, Trash2, Gift, Calendar, Activity, Database, Server, Globe, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}><Icon className="h-5 w-5" /></div>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
  </motion.div>
);

const UserRow = ({ user, isSelected, onClick }) => (
  <tr onClick={onClick} className={`cursor-pointer hover:bg-muted/30 transition-colors ${isSelected ? 'bg-muted/50' : ''}`}>
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <div>
          <p className="font-medium text-sm truncate max-w-[200px]">{user.email}</p>
          {user.name && <p className="text-xs text-muted-foreground">{user.name}</p>}
        </div>
        {user.is_admin && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-500">
            <Shield className="h-2.5 w-2.5" />Admin
          </span>
        )}
      </div>
    </td>
    <td className="px-4 py-3">
      {user.subscription?.status === 'active' ? (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500"><CheckCircle className="h-3 w-3" />Pro</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">Free</span>
      )}
    </td>
    <td className="px-4 py-3 text-sm text-muted-foreground">{user.conversation_count}</td>
    <td className="px-4 py-3 text-sm text-muted-foreground">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
  </tr>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASending, setTwoFASending] = useState(false);
  const [twoFAVerifying, setTwoFAVerifying] = useState(false);
  const [twoFACodeSent, setTwoFACodeSent] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'discounts'
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [subAction, setSubAction] = useState('activate');
  const [subDays, setSubDays] = useState('30');
  const [subReason, setSubReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Discount code form
  const [newDiscountCode, setNewDiscountCode] = useState('');
  const [newDiscountType, setNewDiscountType] = useState('percentage');
  const [newDiscountValue, setNewDiscountValue] = useState('');
  const [newDiscountApplies, setNewDiscountApplies] = useState('both');
  const [newDiscountMaxUses, setNewDiscountMaxUses] = useState('');
  const [newDiscountSingleUse, setNewDiscountSingleUse] = useState(false);
  
  // Admin management
  const [showSetAdminModal, setShowSetAdminModal] = useState(false);
  const [adminReason, setAdminReason] = useState('');
  
  // Create user
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [newUserSubscription, setNewUserSubscription] = useState('');
  
  // Health Status
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthAutoRefresh, setHealthAutoRefresh] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API}/admin/check`);
        setIsAdmin(res.data.is_admin);
        setRequires2FA(res.data.requires_2fa);
        setIs2FAVerified(res.data.is_2fa_verified);
        if (res.data.is_admin && res.data.is_2fa_verified) {
          loadStats();
          loadUsers();
        }
      } catch {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  const send2FACode = async () => {
    setTwoFASending(true);
    try {
      await axios.post(`${API}/admin/2fa/send`);
      setTwoFACodeSent(true);
      toast.success('2FA code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setTwoFASending(false);
    }
  };

  const verify2FACode = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setTwoFAVerifying(true);
    try {
      const response = await axios.post(`${API}/admin/2fa/verify`, { code: twoFACode });
      if (response.data.success) {
        setIs2FAVerified(true);
        setRequires2FA(false);
        setTwoFACode('');
        setTwoFACodeSent(false);
        toast.success('2FA verified successfully');
        // Small delay to ensure state updates before loading data
        setTimeout(() => {
          loadStats();
          loadUsers();
        }, 100);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid code';
      toast.error(errorMsg);
      // If code expired or not found, reset the send state so user can request new code
      if (errorMsg.includes('expired') || errorMsg.includes('No 2FA code')) {
        setTwoFACodeSent(false);
        setTwoFACode('');
      }
    } finally {
      setTwoFAVerifying(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setRequires2FA(true);
        setIs2FAVerified(false);
      }
    }
  };

  const loadHealthStatus = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health-status`);
      setHealthStatus(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setRequires2FA(true);
        setIs2FAVerified(false);
      } else {
        toast.error('Failed to load health status');
      }
    } finally {
      setHealthLoading(false);
    }
  }, []);

  // Auto-refresh health status every 30 seconds when enabled
  useEffect(() => {
    if (healthAutoRefresh && isAdmin && is2FAVerified && activeTab === 'health') {
      const interval = setInterval(loadHealthStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [healthAutoRefresh, isAdmin, is2FAVerified, activeTab, loadHealthStatus]);

  // Load health status when switching to health tab
  useEffect(() => {
    if (activeTab === 'health' && isAdmin && is2FAVerified && !healthStatus) {
      loadHealthStatus();
    }
  }, [activeTab, isAdmin, is2FAVerified, healthStatus, loadHealthStatus]);

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '15' });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/admin/users?${params}`);
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
      setTotalPages(res.data.pages);
    } catch (err) {
      if (err.response?.status === 403) {
        setRequires2FA(true);
        setIs2FAVerified(false);
      }
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    if (isAdmin && is2FAVerified) loadUsers();
  }, [isAdmin, is2FAVerified, loadUsers]);

  const loadUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetails(res.data);
    } catch {
      toast.error('Failed to load user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    loadUserDetails(user.id);
  };

  const handleCreditAdjust = async () => {
    if (!creditAmount || !creditReason) return toast.error('Fill all fields');
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/credits/adjust`, { user_id: selectedUser.id, amount: parseFloat(creditAmount), reason: creditReason });
      toast.success('Credits adjusted');
      setShowCreditModal(false);
      setCreditAmount('');
      setCreditReason('');
      loadUserDetails(selectedUser.id);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubOverride = async () => {
    if (!subReason) return toast.error('Provide a reason');
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/subscription/override`, { user_id: selectedUser.id, action: subAction, days: parseInt(subDays), reason: subReason });
      toast.success('Subscription updated');
      setShowSubModal(false);
      setSubReason('');
      loadUserDetails(selectedUser.id);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const loadDiscountCodes = async () => {
    setDiscountLoading(true);
    try {
      const res = await axios.get(`${API}/admin/discount-codes`);
      setDiscountCodes(res.data.codes);
    } catch (err) {
      toast.error('Failed to load discount codes');
    } finally {
      setDiscountLoading(false);
    }
  };

  const createDiscountCode = async () => {
    if (!newDiscountCode || !newDiscountValue) {
      return toast.error('Code and value are required');
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        code: newDiscountCode,
        discount_type: newDiscountType,
        discount_value: parseFloat(newDiscountValue),
        applies_to: newDiscountApplies,
        max_uses: newDiscountMaxUses ? parseInt(newDiscountMaxUses) : null,
        is_single_use: newDiscountSingleUse
      });
      toast.success('Discount code created');
      setShowDiscountModal(false);
      setNewDiscountCode('');
      setNewDiscountValue('');
      setNewDiscountMaxUses('');
      setNewDiscountSingleUse(false);
      loadDiscountCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`);
      toast.success('Discount code deleted');
      loadDiscountCodes();
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  const toggleDiscountCode = async (code) => {
    try {
      await axios.patch(`${API}/admin/discount-codes/${code.id}`, { active: !code.active });
      toast.success(code.active ? 'Code deactivated' : 'Code activated');
      loadDiscountCodes();
    } catch (err) {
      toast.error('Failed to update code');
    }
  };

  const handleSetAdmin = async () => {
    if (!adminReason) return toast.error('Provide a reason');
    setActionLoading(true);
    try {
      const newAdminStatus = !userDetails?.is_admin;
      await axios.post(`${API}/admin/users/${selectedUser.id}/set-admin`, {
        user_id: selectedUser.id,
        is_admin: newAdminStatus,
        reason: adminReason
      });
      toast.success(newAdminStatus ? 'Admin access granted' : 'Admin access removed');
      setShowSetAdminModal(false);
      setAdminReason('');
      loadUserDetails(selectedUser.id);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update admin status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      return toast.error('Email and password are required');
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/create`, {
        email: newUserEmail,
        password: newUserPassword,
        name: newUserName || null,
        is_admin: newUserIsAdmin,
        subscription_type: newUserSubscription || null
      });
      toast.success('User created successfully');
      setShowCreateUserModal(false);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserName('');
      setNewUserIsAdmin(false);
      setNewUserSubscription('');
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'discounts' && isAdmin && is2FAVerified) {
      loadDiscountCodes();
    }
  }, [activeTab, isAdmin, is2FAVerified]);

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">You do not have admin privileges.</p>
          <Button onClick={() => navigate('/chat')}><ArrowLeft className="h-4 w-4 mr-2" />Back to Arlo</Button>
        </div>
      </div>
    );
  }

  // 2FA Verification Screen
  if (requires2FA && !is2FAVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full bg-card border border-border rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="h-8 w-8 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Admin Verification</h1>
            <p className="text-muted-foreground">Two-factor authentication required for admin access.</p>
          </div>
          
          {!twoFACodeSent ? (
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground">Click below to receive a verification code at your admin email.</p>
              <Button onClick={send2FACode} disabled={twoFASending} className="w-full gap-2">
                {twoFASending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send Verification Code
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Enter 6-digit code</label>
                <Input
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                />
              </div>
              <Button onClick={verify2FACode} disabled={twoFAVerifying || twoFACode.length !== 6} className="w-full">
                {twoFAVerifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verify Code
              </Button>
              <Button variant="ghost" onClick={send2FACode} disabled={twoFASending} className="w-full text-sm">
                Resend Code
              </Button>
            </div>
          )}
          
          <div className="mt-6 pt-6 border-t border-border">
            <Button variant="ghost" onClick={() => navigate('/chat')} className="w-full"><ArrowLeft className="h-4 w-4 mr-2" />Back to Arlo</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/chat')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /><span className="text-sm hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-500" />
              <h1 className="text-lg font-semibold">Admin Dashboard</h1>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadStats(); loadUsers(); }}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} subtext={`+${stats.new_users_30d} this month`} color="bg-blue-500/10 text-blue-500" />
            <StatCard icon={CreditCard} label="Active Subs" value={stats.active_subscriptions} subtext={`${stats.conversion_rate}% conversion`} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={DollarSign} label="Monthly Rev" value={`$${stats.monthly_revenue}`} subtext="Last 30 days" color="bg-purple-500/10 text-purple-500" />
            <StatCard icon={XCircle} label="Churned" value={stats.cancelled_30d} subtext="Last 30 days" color="bg-red-500/10 text-red-500" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === 'users' ? 'default' : 'outline'} onClick={() => setActiveTab('users')} className="gap-2">
            <Users className="h-4 w-4" />Users
          </Button>
          <Button variant={activeTab === 'discounts' ? 'default' : 'outline'} onClick={() => setActiveTab('discounts')} className="gap-2">
            <Tag className="h-4 w-4" />Discount Codes
          </Button>
          <Button variant={activeTab === 'health' ? 'default' : 'outline'} onClick={() => setActiveTab('health')} className="gap-2">
            <Activity className="h-4 w-4" />Health Status
          </Button>
        </div>

        {activeTab === 'users' ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadUsers(); }} className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9" />
                </div>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-border rounded-lg bg-background text-sm">
                  <option value="">All</option>
                  <option value="subscribed">Subscribed</option>
                  <option value="free">Free</option>
                </select>
                <Button type="submit" variant="secondary">Search</Button>
                <Button type="button" onClick={() => setShowCreateUserModal(true)} className="gap-2">
                  <Plus className="h-4 w-4" />Create User
                </Button>
              </form>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">User</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Convos</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((user) => (<UserRow key={user.id} user={user} isSelected={selectedUser?.id === user.id} onClick={() => handleUserClick(user)} />))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between p-4 border-t border-border">
              <p className="text-sm text-muted-foreground">{totalUsers} users</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-sm">{page}/{totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 bg-card border border-border rounded-xl overflow-hidden sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
            {!selectedUser ? (
              <div className="text-center py-12 px-4"><Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">Select a user</p></div>
            ) : detailsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : userDetails ? (
              <div>
                {/* User Header */}
                <div className="p-4 border-b border-border bg-muted/30">
                  <h3 className="font-semibold truncate">{userDetails.user.email}</h3>
                  {userDetails.user.onboarding?.full_profile?.name && (<p className="text-sm text-muted-foreground">{userDetails.user.onboarding.full_profile.name}</p>)}
                  <p className="text-xs text-muted-foreground mt-1">Joined: {new Date(userDetails.user.created_at).toLocaleDateString()}</p>
                </div>

                {/* Billing Summary Card */}
                <div className="p-4 border-b border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4" />Billing Summary</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Lifetime Value</p>
                      <p className="text-lg font-bold text-emerald-500">${userDetails.billing_summary?.total_lifetime_value || 0}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Transactions</p>
                      <p className="text-lg font-bold">{userDetails.billing_summary?.total_transactions || 0}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Discounts Used</p>
                      <p className="text-lg font-bold text-blue-500">${userDetails.billing_summary?.total_discounts_received || 0}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Credits</p>
                      <p className="text-lg font-bold">${userDetails.user.credits?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                  {userDetails.billing_summary?.stripe_customer_id && (
                    <div className="mt-3 p-2 bg-muted/20 rounded text-xs">
                      <span className="text-muted-foreground">Stripe ID: </span>
                      <span className="font-mono">{userDetails.billing_summary.stripe_customer_id}</span>
                    </div>
                  )}
                </div>

                {/* Subscription Status */}
                <div className="p-4 border-b border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><CreditCard className="h-4 w-4" />Subscription</h4>
                  {userDetails.subscription?.status === 'active' ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-emerald-500 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          {userDetails.billing_summary?.is_complimentary ? (
                            userDetails.billing_summary?.comp_type === 'forever' ? 'Comp Pro (Forever)' : 'Comp Pro (1 Year)'
                          ) : 'Pro Active'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>Plan: <span className="font-medium">{userDetails.subscription.plan_id}</span></p>
                        {userDetails.subscription.current_period_end && (
                          <p>{userDetails.subscription.cancel_at_period_end ? 'Expires' : 'Renews'}: {new Date(userDetails.subscription.current_period_end).toLocaleDateString()}</p>
                        )}
                        {userDetails.subscription.source && (
                          <p>Source: <span className="font-medium capitalize">{userDetails.subscription.source}</span></p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Free Plan</p>
                  )}
                  <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setShowSubModal(true)}>
                    <CreditCard className="h-4 w-4 mr-2" />Manage Subscription
                  </Button>
                </div>

                {/* Transaction History */}
                <div className="p-4 border-b border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><RefreshCw className="h-4 w-4" />Payment History</h4>
                  {userDetails.transactions?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {userDetails.transactions.map((tx, idx) => (
                        <div key={tx.id || idx} className="p-2 bg-muted/20 rounded-lg text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">${tx.amount}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${tx.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-500' : tx.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-red-500/20 text-red-500'}`}>
                              {tx.payment_status}
                            </span>
                          </div>
                          <p className="text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                          {tx.discount_code && (
                            <p className="text-blue-500">Code: {tx.discount_code.code} (-${tx.discount_code.discount_amount})</p>
                          )}
                          {tx.session_id && (
                            <p className="text-muted-foreground font-mono truncate" title={tx.session_id}>Session: {tx.session_id.slice(0, 20)}...</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No transactions</p>
                  )}
                </div>

                {/* Usage Stats */}
                <div className="p-4 border-b border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Usage</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-lg font-bold">{userDetails.stats.conversations}</p>
                      <p className="text-xs text-muted-foreground">Conversations</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-lg font-bold">{userDetails.stats.messages}</p>
                      <p className="text-xs text-muted-foreground">Messages</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-lg font-bold">{userDetails.stats.memories}</p>
                      <p className="text-xs text-muted-foreground">Memories</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-lg font-bold">{userDetails.stats.knowledge_docs}</p>
                      <p className="text-xs text-muted-foreground">Documents</p>
                    </div>
                  </div>
                </div>

                {/* Admin Actions */}
                <div className="p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><Shield className="h-4 w-4" />Admin History</h4>
                  {userDetails.admin_actions?.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {userDetails.admin_actions.map((action, idx) => (
                        <div key={action.id || idx} className="p-2 bg-muted/20 rounded text-xs">
                          <p className="font-medium">{action.action.replace(/_/g, ' ')}</p>
                          <p className="text-muted-foreground">{action.admin_email} • {new Date(action.created_at).toLocaleDateString()}</p>
                          {action.reason && <p className="text-muted-foreground">Reason: {action.reason}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No admin actions</p>
                  )}
                  <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setShowCreditModal(true)}>
                    <DollarSign className="h-4 w-4 mr-2" />Adjust Credits
                  </Button>
                  <Button 
                    variant={userDetails?.is_admin ? "destructive" : "outline"} 
                    size="sm" 
                    className="w-full mt-2" 
                    onClick={() => setShowSetAdminModal(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {userDetails?.is_admin ? 'Remove Admin Access' : 'Grant Admin Access'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        ) : activeTab === 'discounts' ? (
        /* Discount Codes Tab */
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2"><Tag className="h-5 w-5" />Discount Codes</h2>
            <Button size="sm" onClick={() => setShowDiscountModal(true)}><Plus className="h-4 w-4 mr-2" />Create Code</Button>
          </div>
          
          {discountLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : discountCodes.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No discount codes yet</p>
              <Button className="mt-4" onClick={() => setShowDiscountModal(true)}><Plus className="h-4 w-4 mr-2" />Create Your First Code</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Code</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Discount</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Applies To</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Usage</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {discountCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <span className="font-mono font-medium bg-muted px-2 py-1 rounded">{code.code}</span>
                        {code.is_single_use && <span className="ml-2 text-xs text-muted-foreground">(single-use)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {code.discount_type === 'percentage' ? `${code.discount_value}%` : `$${code.discount_value}`} off
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{code.applies_to}</td>
                      <td className="px-4 py-3 text-sm">
                        {code.current_uses}{code.max_uses ? `/${code.max_uses}` : ''} uses
                      </td>
                      <td className="px-4 py-3">
                        {code.active ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500">
                            <CheckCircle className="h-3 w-3" />Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => toggleDiscountCode(code)}>
                            {code.active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteDiscountCode(code.id)} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        ) : activeTab === 'health' ? (
        /* Health Status Tab */
        <div className="space-y-6">
          {/* Health Status Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                healthStatus?.overall === 'healthy' ? 'bg-emerald-500/10' :
                healthStatus?.overall === 'degraded' ? 'bg-yellow-500/10' :
                healthStatus?.overall === 'unhealthy' ? 'bg-red-500/10' : 'bg-muted'
              }`}>
                <Activity className={`h-6 w-6 ${
                  healthStatus?.overall === 'healthy' ? 'text-emerald-500' :
                  healthStatus?.overall === 'degraded' ? 'text-yellow-500' :
                  healthStatus?.overall === 'unhealthy' ? 'text-red-500' : 'text-muted-foreground'
                }`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold">System Health</h2>
                <p className="text-sm text-muted-foreground">
                  {healthStatus?.timestamp ? `Last checked: ${new Date(healthStatus.timestamp).toLocaleTimeString()}` : 'Not yet checked'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={healthAutoRefresh}
                  onChange={(e) => setHealthAutoRefresh(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                />
                Auto-refresh (30s)
              </label>
              <Button variant="outline" size="sm" onClick={loadHealthStatus} disabled={healthLoading}>
                {healthLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh
              </Button>
            </div>
          </div>

          {/* Overall Status Banner */}
          {healthStatus && (
            <div className={`p-4 rounded-xl border ${
              healthStatus.overall === 'healthy' ? 'bg-emerald-500/10 border-emerald-500/20' :
              healthStatus.overall === 'degraded' ? 'bg-yellow-500/10 border-yellow-500/20' :
              'bg-red-500/10 border-red-500/20'
            }`}>
              <div className="flex items-center gap-3">
                {healthStatus.overall === 'healthy' ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : healthStatus.overall === 'degraded' ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className={`font-medium ${
                    healthStatus.overall === 'healthy' ? 'text-emerald-500' :
                    healthStatus.overall === 'degraded' ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {healthStatus.overall === 'healthy' ? 'All Systems Operational' :
                     healthStatus.overall === 'degraded' ? 'Partial System Degradation' :
                     'System Issues Detected'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {healthStatus.overall === 'healthy' ? 'All services are running normally.' :
                     healthStatus.overall === 'degraded' ? 'Some services may be experiencing issues.' :
                     'One or more critical services are down.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Service Cards */}
          {healthLoading && !healthStatus ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : healthStatus?.services ? (
            <div className="grid md:grid-cols-3 gap-4">
              {/* Backend Service */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      healthStatus.services.backend?.status === 'healthy' ? 'bg-emerald-500/10' :
                      healthStatus.services.backend?.status === 'degraded' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                    }`}>
                      <Server className={`h-5 w-5 ${
                        healthStatus.services.backend?.status === 'healthy' ? 'text-emerald-500' :
                        healthStatus.services.backend?.status === 'degraded' ? 'text-yellow-500' : 'text-red-500'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">{healthStatus.services.backend?.name || 'Backend API'}</p>
                      <p className="text-xs text-muted-foreground">FastAPI Server</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    healthStatus.services.backend?.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-500' :
                    healthStatus.services.backend?.status === 'degraded' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {healthStatus.services.backend?.status || 'unknown'}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Latency</span>
                    <span className="font-mono">{healthStatus.services.backend?.latency_ms ?? '-'} ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{healthStatus.services.backend?.details}</p>
                </div>
              </div>

              {/* Database Service */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      healthStatus.services.database?.status === 'healthy' ? 'bg-emerald-500/10' :
                      healthStatus.services.database?.status === 'degraded' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                    }`}>
                      <Database className={`h-5 w-5 ${
                        healthStatus.services.database?.status === 'healthy' ? 'text-emerald-500' :
                        healthStatus.services.database?.status === 'degraded' ? 'text-yellow-500' : 'text-red-500'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">{healthStatus.services.database?.name || 'Database'}</p>
                      <p className="text-xs text-muted-foreground">MongoDB</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    healthStatus.services.database?.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-500' :
                    healthStatus.services.database?.status === 'degraded' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {healthStatus.services.database?.status || 'unknown'}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Latency</span>
                    <span className="font-mono">{healthStatus.services.database?.latency_ms ?? '-'} ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{healthStatus.services.database?.details}</p>
                </div>
              </div>

              {/* Frontend Service */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      healthStatus.services.frontend?.status === 'healthy' ? 'bg-emerald-500/10' :
                      healthStatus.services.frontend?.status === 'degraded' ? 'bg-yellow-500/10' :
                      healthStatus.services.frontend?.status === 'unknown' ? 'bg-gray-500/10' : 'bg-red-500/10'
                    }`}>
                      <Globe className={`h-5 w-5 ${
                        healthStatus.services.frontend?.status === 'healthy' ? 'text-emerald-500' :
                        healthStatus.services.frontend?.status === 'degraded' ? 'text-yellow-500' :
                        healthStatus.services.frontend?.status === 'unknown' ? 'text-gray-400' : 'text-red-500'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">{healthStatus.services.frontend?.name || 'Frontend'}</p>
                      <p className="text-xs text-muted-foreground">React App</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    healthStatus.services.frontend?.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-500' :
                    healthStatus.services.frontend?.status === 'degraded' ? 'bg-yellow-500/10 text-yellow-500' :
                    healthStatus.services.frontend?.status === 'unknown' ? 'bg-gray-500/10 text-gray-400' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {healthStatus.services.frontend?.status || 'unknown'}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Latency</span>
                    <span className="font-mono">{healthStatus.services.frontend?.latency_ms ?? '-'} ms</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{healthStatus.services.frontend?.details}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Click Refresh to check system health</p>
            </div>
          )}
        </div>
        ) : null}
      </main>

      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Credits</DialogTitle><DialogDescription>Add or deduct credits for {selectedUser?.email}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="text-sm font-medium mb-1 block">Amount (negative to deduct)</label><Input type="number" step="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="10.00" /></div>
            <div><label className="text-sm font-medium mb-1 block">Reason</label><Input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Billing issue refund" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditModal(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleCreditAdjust} disabled={actionLoading}>{actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSubModal} onOpenChange={setShowSubModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Subscription</DialogTitle><DialogDescription>Override subscription for {selectedUser?.email}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="text-sm font-medium mb-1 block">Action</label>
              <select value={subAction} onChange={(e) => setSubAction(e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg bg-background">
                <option value="activate">Activate Pro (Custom Days)</option>
                <option value="comp_forever">Comp Pro (Forever)</option>
                <option value="comp_1year">Comp Pro (1 Year)</option>
                <option value="extend">Extend Subscription</option>
                <option value="cancel">Cancel Immediately</option>
              </select>
            </div>
            {subAction === 'comp_forever' && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-sm text-emerald-500 flex items-center gap-2"><Gift className="h-4 w-4" />Grants lifetime Pro access - never expires</p>
              </div>
            )}
            {subAction === 'comp_1year' && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-500 flex items-center gap-2"><Calendar className="h-4 w-4" />Grants 1 year Pro access - user will be prompted 30 days before expiry to subscribe</p>
              </div>
            )}
            {(subAction === 'activate' || subAction === 'extend') && (<div><label className="text-sm font-medium mb-1 block">Days</label><Input type="number" value={subDays} onChange={(e) => setSubDays(e.target.value)} placeholder="30" /></div>)}
            <div><label className="text-sm font-medium mb-1 block">Reason</label><Input value={subReason} onChange={(e) => setSubReason(e.target.value)} placeholder="Customer support" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubModal(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleSubOverride} disabled={actionLoading} className={subAction === 'cancel' ? 'bg-destructive hover:bg-destructive/90' : subAction.startsWith('comp') ? 'bg-emerald-500 hover:bg-emerald-600' : ''}>
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {subAction === 'activate' ? 'Activate' : subAction === 'extend' ? 'Extend' : subAction === 'cancel' ? 'Cancel' : subAction === 'comp_forever' ? 'Grant Forever' : 'Grant 1 Year'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Discount Code Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Discount Code</DialogTitle>
            <DialogDescription>Create a new discount code for subscriptions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Code</label>
              <Input 
                value={newDiscountCode} 
                onChange={(e) => setNewDiscountCode(e.target.value.toUpperCase())} 
                placeholder="SAVE20" 
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Discount Type</label>
                <select 
                  value={newDiscountType} 
                  onChange={(e) => setNewDiscountType(e.target.value)} 
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Value</label>
                <Input 
                  type="number" 
                  value={newDiscountValue} 
                  onChange={(e) => setNewDiscountValue(e.target.value)} 
                  placeholder={newDiscountType === 'percentage' ? '20' : '10'}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Applies To</label>
              <select 
                value={newDiscountApplies} 
                onChange={(e) => setNewDiscountApplies(e.target.value)} 
                className="w-full px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="both">Both Monthly & Yearly</option>
                <option value="monthly">Monthly Only</option>
                <option value="yearly">Yearly Only</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Max Uses (optional)</label>
                <Input 
                  type="number" 
                  value={newDiscountMaxUses} 
                  onChange={(e) => setNewDiscountMaxUses(e.target.value)} 
                  placeholder="Unlimited"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="singleUse"
                  checked={newDiscountSingleUse}
                  onChange={(e) => setNewDiscountSingleUse(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                />
                <label htmlFor="singleUse" className="text-sm">Single use per user</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={createDiscountCode} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Admin Modal */}
      <Dialog open={showSetAdminModal} onOpenChange={setShowSetAdminModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {userDetails?.is_admin ? 'Remove Admin Access' : 'Grant Admin Access'}
            </DialogTitle>
            <DialogDescription>
              {userDetails?.is_admin 
                ? `Remove admin privileges from ${selectedUser?.email}` 
                : `Grant admin privileges to ${selectedUser?.email}`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Reason</label>
              <Input 
                value={adminReason} 
                onChange={(e) => setAdminReason(e.target.value)} 
                placeholder="Reason for admin status change"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetAdminModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSetAdmin} 
              disabled={actionLoading}
              variant={userDetails?.is_admin ? "destructive" : "default"}
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {userDetails?.is_admin ? 'Remove Admin' : 'Grant Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Create a new user account with optional admin privileges</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Email *</label>
              <Input 
                type="email"
                value={newUserEmail} 
                onChange={(e) => setNewUserEmail(e.target.value)} 
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Password *</label>
              <Input 
                type="password"
                value={newUserPassword} 
                onChange={(e) => setNewUserPassword(e.target.value)} 
                placeholder="Secure password"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name (optional)</label>
              <Input 
                value={newUserName} 
                onChange={(e) => setNewUserName(e.target.value)} 
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Subscription Type</label>
              <select 
                value={newUserSubscription} 
                onChange={(e) => setNewUserSubscription(e.target.value)} 
                className="w-full px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="">Free Plan</option>
                <option value="pro_monthly">Pro Monthly</option>
                <option value="pro_yearly">Pro Yearly</option>
                <option value="comp_forever">Comp Pro (Forever)</option>
                <option value="comp_1year">Comp Pro (1 Year)</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="newUserAdmin"
                checked={newUserIsAdmin}
                onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                className="w-4 h-4 rounded border-border"
              />
              <label htmlFor="newUserAdmin" className="text-sm">Grant admin privileges</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Admin Modal */}
      <Dialog open={showSetAdminModal} onOpenChange={setShowSetAdminModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{userDetails?.is_admin ? 'Remove Admin Access' : 'Grant Admin Access'}</DialogTitle>
            <DialogDescription>
              {userDetails?.is_admin 
                ? `Remove admin privileges from ${selectedUser?.email}. They will no longer be able to access the admin dashboard.`
                : `Grant admin privileges to ${selectedUser?.email}. They will be able to access the admin dashboard and manage users.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={`p-4 rounded-lg ${userDetails?.is_admin ? 'bg-red-500/10 border border-red-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
              <p className={`text-sm font-medium ${userDetails?.is_admin ? 'text-red-500' : 'text-emerald-500'}`}>
                <Shield className="h-4 w-4 inline mr-2" />
                {userDetails?.is_admin 
                  ? 'Warning: This user will lose all admin access'
                  : 'This user will gain full admin access'
                }
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Reason (required)</label>
              <Input 
                value={adminReason} 
                onChange={(e) => setAdminReason(e.target.value)} 
                placeholder="Why are you making this change?" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetAdminModal(false)} disabled={actionLoading}>Cancel</Button>
            <Button 
              onClick={handleSetAdmin} 
              disabled={actionLoading || !adminReason}
              className={userDetails?.is_admin ? 'bg-destructive hover:bg-destructive/90' : 'bg-emerald-500 hover:bg-emerald-600'}
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {userDetails?.is_admin ? 'Remove Admin' : 'Grant Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Create a new user account with optional subscription</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Email *</label>
              <Input 
                type="email"
                value={newUserEmail} 
                onChange={(e) => setNewUserEmail(e.target.value)} 
                placeholder="user@example.com" 
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Password *</label>
              <Input 
                type="password"
                value={newUserPassword} 
                onChange={(e) => setNewUserPassword(e.target.value)} 
                placeholder="••••••••" 
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name (optional)</label>
              <Input 
                value={newUserName} 
                onChange={(e) => setNewUserName(e.target.value)} 
                placeholder="John Doe" 
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Subscription</label>
              <select 
                value={newUserSubscription} 
                onChange={(e) => setNewUserSubscription(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="">Free Plan</option>
                <option value="pro_monthly">Pro Monthly (30 days)</option>
                <option value="comp_1year">Comp Pro (1 Year)</option>
                <option value="comp_forever">Comp Pro (Forever)</option>
              </select>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <input
                type="checkbox"
                id="newUserAdmin"
                checked={newUserIsAdmin}
                onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                className="w-4 h-4 rounded border-border"
              />
              <label htmlFor="newUserAdmin" className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500" />
                Grant admin access
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserModal(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={actionLoading || !newUserEmail || !newUserPassword}>
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
